import React, { useEffect, useMemo, useState } from 'react';
import { shopModel } from '../Axios/AxiosConfig';
import {
    Box,
    Card,
    CardContent,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Divider,
    Button,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CloseIcon from '@mui/icons-material/Close';

interface Coupon {
    id: string;
    couponName: string;
    couponType: 'fixedAmount' | 'percentage' | string;
    discount?: number;
    cashRedeem?: number;
    minValue?: number;
    maxUsage?: number;
    applicableTo: string[];
    valid: boolean;
    expiration?: string;
    Public?: boolean;
    userId?: string;
}

type SectionFilter = 'all' | 'activePublic' | 'activePrivate' | 'expired';

const CouponSections: React.FC = () => {

    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [sectionFilter, setSectionFilter] = useState<SectionFilter>('all');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);
    const [copyMessage, setCopyMessage] = useState<string | null>(null);

    const [newName, setNewName] = useState('');
    const [newUserId, setNewUserId] = useState('');
    const [newCouponType, setNewCouponType] = useState<'fixedAmount' | 'percentage'>('percentage');
    const [newDiscount, setNewDiscount] = useState('');
    const [newCashRedeem, setNewCashRedeem] = useState('');
    const [newMinValue, setNewMinValue] = useState('');
    const [newMaxUsage, setNewMaxUsage] = useState('');
    const [newApplicableTo, setNewApplicableTo] = useState<string[]>([]);
    const [newExpirationDate, setNewExpirationDate] = useState<Date | null>(null);

    const [newPublic, setNewPublic] = useState(true);
    const [newValid, setNewValid] = useState(true);
    const [newTypeField, setNewTypeField] = useState('');
    const [newInfo, setNewInfo] = useState('');

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);

    const [editName, setEditName] = useState('');
    const [editUserId, setEditUserId] = useState('');
    const [editCouponType, setEditCouponType] = useState<'fixedAmount' | 'percentage'>('percentage');
    const [editDiscount, setEditDiscount] = useState('');
    const [editCashRedeem, setEditCashRedeem] = useState('');
    const [editMinValue, setEditMinValue] = useState('');
    const [editMaxUsage, setEditMaxUsage] = useState('');
    const [editApplicableTo, setEditApplicableTo] = useState<string[]>([]);
    const [editExpirationDate, setEditExpirationDate] = useState<Date | null>(null);
    const [editPublic, setEditPublic] = useState(true);
    const [editValid, setEditValid] = useState(true);
    const [editTypeField, setEditTypeField] = useState('');
    const [editInfo, setEditInfo] = useState('');

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    useEffect(() => {
        const fetchCoupons = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await shopModel.get('/getCoupons');
                const data: Coupon[] = res.data || [];
                setCoupons(data);
            } catch (err: any) {
                setError(
                    err.response?.data?.error ||
                    err.message ||
                    'Failed to load coupons'
                );
            } finally {
                setLoading(false);
            }
        };

        fetchCoupons();
    }, []);

    const couponTypes = useMemo(
        () =>
            Array.from(
                new Set(
                    coupons
                        .map(c => c.couponType)
                        .filter((t): t is string => Boolean(t))
                )
            ).sort(),
        [coupons]
    );

    const filteredCoupons = useMemo(() => {
        const now = new Date();
        return coupons.filter(coupon => {
            const expirationDate = coupon.expiration ? new Date(coupon.expiration) : null;
            const isExpired = expirationDate !== null && expirationDate < now;

            let matchesSection = true;
            switch (sectionFilter) {
                case 'activePublic':
                    matchesSection = !isExpired && coupon.Public === true;
                    break;
                case 'activePrivate':
                    matchesSection = !isExpired && coupon.Public === false;
                    break;
                case 'expired':
                    matchesSection = isExpired;
                    break;
                default:
                    matchesSection = true;
            }

            const matchesType =
                typeFilter === 'all' || coupon.couponType === typeFilter;

            const matchesSearch =
                !searchTerm ||
                coupon.couponName.toLowerCase().includes(searchTerm.toLowerCase());

            return matchesSection && matchesType && matchesSearch;
        });
    }, [coupons, sectionFilter, typeFilter, searchTerm]);

    const couponStats = useMemo(() => {
        const now = new Date();
        let active = 0;
        let expired = 0;

        coupons.forEach(coupon => {
            const expirationDate = coupon.expiration ? new Date(coupon.expiration) : null;
            const isExpired = expirationDate !== null && expirationDate < now;
            if (isExpired) {
                expired += 1;
            } else {
                active += 1;
            }
        });

        return {
            total: coupons.length,
            active,
            expired,
        };
    }, [coupons]);

    const resetNewCouponForm = () => {
        setNewName('');
        setNewUserId('');
        setNewCouponType('percentage');
        setNewDiscount('');
        setNewCashRedeem('');
        setNewMinValue('');
        setNewMaxUsage('');
        setNewApplicableTo([]);
        setNewExpirationDate(null);

        setNewPublic(true);
        setNewValid(true);
        setNewTypeField('');
        setNewInfo('');
    };

    const handleOpenAddDialog = () => {
        setAddError(null);
        resetNewCouponForm();
        setAddDialogOpen(true);
    };

    const handleCreateCoupon = async () => {

        if (!newExpirationDate) {
            setAddError('Expiration date is required');
            return;
        }

        const applicableList = newApplicableTo;

        const trimmedUserId = newUserId.trim();
        let finalName = newName.trim();

        if (!finalName && trimmedUserId) {
            const base = trimmedUserId.slice(0, 6);
            let suffix = '';

            if (newCouponType === 'percentage' && newDiscount) {
                const n = Number(newDiscount);
                if (!Number.isNaN(n) && n > 0) {
                    suffix = String(n);
                }
            } else if (newCouponType === 'fixedAmount' && newCashRedeem) {
                const n = Number(newCashRedeem);
                if (!Number.isNaN(n) && n > 0) {
                    suffix = String(n);
                }
            }

            finalName = suffix ? `${base}-${suffix}` : base;
        }

        if (!finalName) {
            setAddError('Coupon name or user ID is required');
            return;
        }

        const payload: any = {
            couponName: finalName,
            couponType: newCouponType,

            discount:
                newCouponType === 'percentage' && newDiscount
                    ? Number(newDiscount)
                    : undefined,
            cashRedeem:
                newCouponType === 'fixedAmount' && newCashRedeem
                    ? Number(newCashRedeem)
                    : undefined,
            minValue:
                newCouponType === 'fixedAmount' && newMinValue
                    ? Number(newMinValue)
                    : undefined,
            maxUsage: newMaxUsage ? Number(newMaxUsage) : undefined,
            applicableTo: applicableList,
            type: newTypeField || 'Shop',
            valid: newValid,
            expiration: newExpirationDate.toISOString(),
            Public: newPublic,
            info: newInfo || undefined,
            userId: trimmedUserId || undefined,
        };

        setCreating(true);
        setAddError(null);
        try {
            const res = await shopModel.post('/addCoupons', payload);
            const created: Coupon = res.data;
            setCoupons(prev => [...prev, created]);
            setAddDialogOpen(false);
            resetNewCouponForm();
            setSelectedCoupon(created);
            setDialogOpen(true);

        } catch (err: any) {
            setAddError(
                err.response?.data?.error ||
                err.message ||
                'Failed to create coupon'
            );
        } finally {
            setCreating(false);
        }
    };

    const handleCopyCode = async () => {
        if (!selectedCoupon?.couponName) return;
        try {
            await navigator.clipboard.writeText(selectedCoupon.couponName);
            setCopyMessage('Code copied');
        } catch {
            setCopyMessage('Unable to copy');
        }

        setTimeout(() => setCopyMessage(null), 2000);
    };

    const openEditDialogFromCoupon = (coupon: Coupon) => {
        setEditError(null);
        setEditName(coupon.couponName || '');
        setEditUserId(coupon.userId || '');
        const cType = coupon.couponType === 'fixedAmount' ? 'fixedAmount' : 'percentage';
        setEditCouponType(cType);
        setEditDiscount(
            cType === 'percentage' && typeof coupon.discount === 'number'
                ? String(coupon.discount)
                : ''
        );
        setEditCashRedeem(
            cType === 'fixedAmount' && typeof coupon.cashRedeem === 'number'
                ? String(coupon.cashRedeem)
                : ''
        );
        setEditMinValue(
            cType === 'fixedAmount' && typeof coupon.minValue === 'number'
                ? String(coupon.minValue)
                : ''
        );
        setEditMaxUsage(
            typeof coupon.maxUsage === 'number' ? String(coupon.maxUsage) : ''
        );
        setEditApplicableTo(Array.isArray(coupon.applicableTo) ? coupon.applicableTo : []);
        setEditExpirationDate(
            coupon.expiration && !Number.isNaN(Date.parse(coupon.expiration))
                ? new Date(coupon.expiration)
                : null
        );
        setEditPublic(Boolean(coupon.Public));
        setEditValid(Boolean(coupon.valid));
        setEditTypeField((coupon as any).type || '');
        setEditInfo((coupon as any).info || '');
        setEditDialogOpen(true);
    };

    const handleToggleSelectAll = () => {
        if (selectedIds.size === filteredCoupons.length && filteredCoupons.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredCoupons.map(c => c.id)));
        }
    };

    const handleToggleSelect = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const next = new Set(selectedIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedIds(next);
    };

    const handleDeleteSelected = async () => {
        setDeleting(true);
        setDeleteError(null);
        try {
            const idsToDelete = Array.from(selectedIds);
            const res = await shopModel.post('/deleteCoupons', { ids: idsToDelete });

            const { deleted, failed } = res.data;

            if (deleted && deleted.length > 0) {
                setCoupons(prev => prev.filter(c => !deleted.includes(c.id)));
            }

            if (failed && failed.length > 0) {
                setDeleteError(`Failed to delete some coupons: ${failed.map((f: any) => f.id).join(', ')}`);
            } else {
                setConfirmDeleteDialogOpen(false);
                setSelectedIds(new Set());
            }
        } catch (err: any) {
            setDeleteError(err.response?.data?.error || err.message || 'Failed to delete coupons');
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="40vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ p: 2, pt: 1.5 }}>

                <Box
                    sx={{
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography
                            variant="h5"
                            gutterBottom
                            sx={{ fontWeight: 600, fontFamily: 'Libre Baskerville', color: '#3B362A' }}
                        >
                            Coupons
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            View and filter all coupons by status, visibility and type.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Chip
                                label={`Total: ${couponStats.total}`}
                                size="small"
                                color="default"
                                variant="outlined"
                            />
                            <Chip
                                label={`Active: ${couponStats.active}`}
                                size="small"
                                color="success"
                                variant="outlined"
                            />
                            <Chip
                                label={`Expired: ${couponStats.expired}`}
                                size="small"
                                color="default"
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                    <Box sx={{ alignSelf: 'center', display: 'flex', gap: 1 }}>
                        {selectedIds.size > 0 && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => {
                                    setDeleteError(null);
                                    setConfirmDeleteDialogOpen(true);
                                }}
                            >
                                Delete Selected ({selectedIds.size})
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleOpenAddDialog}
                        >
                            Add Coupon
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                )}

                <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 'none' }}>
                    <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ flex: '1 1 220px', minWidth: 200 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Section</InputLabel>
                                <Select
                                    label="Section"
                                    value={sectionFilter}
                                    onChange={e =>
                                        setSectionFilter(e.target.value as SectionFilter)
                                    }
                                >
                                    <MenuItem value="all">All coupons</MenuItem>
                                    <MenuItem value="activePublic">Active public</MenuItem>
                                    <MenuItem value="activePrivate">Active private</MenuItem>
                                    <MenuItem value="expired">Expired</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ flex: '1 1 220px', minWidth: 200 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Coupon type</InputLabel>
                                <Select
                                    label="Coupon type"
                                    value={typeFilter}
                                    onChange={e => setTypeFilter(e.target.value as string)}
                                >
                                    <MenuItem value="all">All types</MenuItem>
                                    {couponTypes.map(type => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ flex: '2 1 260px', minWidth: 240 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search by name"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </Box>
                    </CardContent>
                </Card>

                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        indeterminate={selectedIds.size > 0 && selectedIds.size < filteredCoupons.length}
                                        checked={filteredCoupons.length > 0 && selectedIds.size === filteredCoupons.length}
                                        onChange={handleToggleSelectAll}
                                    />
                                </TableCell>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Type</strong></TableCell>
                                <TableCell align="right"><strong>Value</strong></TableCell>
                                <TableCell align="right"><strong>Min order</strong></TableCell>
                                <TableCell><strong>Applicable To</strong></TableCell>
                                <TableCell><strong>Visibility</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Expiration</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCoupons.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No coupons found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCoupons.map(coupon => {
                                    const expirationDate =
                                        coupon.expiration && !Number.isNaN(Date.parse(coupon.expiration))
                                            ? new Date(coupon.expiration)
                                            : null;
                                    const isExpired =
                                        expirationDate !== null &&
                                        expirationDate.getTime() < new Date().getTime();

                                    const valueLabel = coupon.couponType === 'percentage'
                                        ? coupon.discount
                                            ? `${coupon.discount}%`
                                            : '-'
                                        : coupon.cashRedeem
                                            ? `₹${coupon.cashRedeem}`
                                            : '-';

                                    return (
                                        <TableRow
                                            key={coupon.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            selected={selectedIds.has(coupon.id)}
                                            onClick={() => {
                                                setSelectedCoupon(coupon);
                                                setDialogOpen(true);
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedIds.has(coupon.id)}
                                                    onClick={(e) => handleToggleSelect(coupon.id, e)}
                                                />
                                            </TableCell>
                                            <TableCell>{coupon.couponName}</TableCell>
                                            <TableCell>{coupon.couponType}</TableCell>
                                            <TableCell align="right">{valueLabel}</TableCell>
                                            <TableCell align="right">
                                                {coupon.minValue ? `₹${coupon.minValue}` : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {coupon.applicableTo && coupon.applicableTo.length > 0
                                                    ? coupon.applicableTo.join(', ')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={coupon.Public ? 'Public' : 'Private'}
                                                    size="small"
                                                    color={coupon.Public ? 'primary' : 'default'}
                                                    variant={coupon.Public ? 'filled' : 'outlined'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={isExpired ? 'Expired' : 'Active'}
                                                    size="small"
                                                    color={isExpired ? 'default' : 'success'}
                                                    variant={isExpired ? 'outlined' : 'filled'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {expirationDate
                                                    ? expirationDate.toLocaleDateString()
                                                    : 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Dialog
                    open={addDialogOpen}
                    onClose={() => {
                        if (!creating) setAddDialogOpen(false);
                    }}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Add Coupon
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    if (!creating) setAddDialogOpen(false);
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent dividers>
                        {addError && (
                            <Box sx={{ mb: 2 }}>
                                <Alert severity="error">{addError}</Alert>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Coupon Name"
                                value={newName}
                                onChange={e => setNewName(e.target.value)}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="User ID (optional)"
                                value={newUserId}
                                onChange={e => setNewUserId(e.target.value)}
                                fullWidth
                                size="small"
                            />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <FormControl sx={{ flex: '1 1 160px', minWidth: 140 }} size="small">
                                    <InputLabel>Coupon Type</InputLabel>
                                    <Select
                                        label="Coupon Type"
                                        value={newCouponType}
                                        onChange={e =>
                                            setNewCouponType(e.target.value as 'fixedAmount' | 'percentage')
                                        }
                                    >
                                        <MenuItem value="percentage">Percentage</MenuItem>
                                        <MenuItem value="fixedAmount">Fixed Amount</MenuItem>
                                    </Select>
                                </FormControl>
                                {newCouponType === 'percentage' ? (
                                    <TextField
                                        label="Discount %"
                                        type="number"
                                        value={newDiscount}
                                        onChange={e => setNewDiscount(e.target.value)}
                                        size="small"
                                        sx={{ flex: '1 1 160px', minWidth: 140 }}
                                    />
                                ) : (
                                    <>
                                        <TextField
                                            label="Cash Redeem (₹)"
                                            type="number"
                                            value={newCashRedeem}
                                            onChange={e => setNewCashRedeem(e.target.value)}
                                            size="small"
                                            sx={{ flex: '1 1 160px', minWidth: 140 }}
                                        />
                                        <TextField
                                            label="Min Order Value (₹)"
                                            type="number"
                                            value={newMinValue}
                                            onChange={e => setNewMinValue(e.target.value)}
                                            size="small"
                                            sx={{ flex: '1 1 160px', minWidth: 140 }}
                                        />
                                    </>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <TextField
                                    label="Max Usage"
                                    type="number"
                                    value={newMaxUsage}
                                    onChange={e => setNewMaxUsage(e.target.value)}
                                    size="small"
                                    sx={{ flex: '1 1 160px', minWidth: 140 }}
                                />
                                <DatePicker
                                    label="Expiration Date"
                                    value={newExpirationDate}
                                    onChange={(date) => setNewExpirationDate(date)}
                                    slotProps={{ textField: { size: 'small', sx: { flex: '2 1 220px', minWidth: 200 } } }}
                                />
                            </Box>
                            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                <InputLabel>Applicable To</InputLabel>
                                <Select
                                    label="Applicable To"
                                    multiple
                                    value={newApplicableTo}
                                    onChange={e => {
                                        const value = e.target.value;
                                        setNewApplicableTo(
                                            typeof value === 'string'
                                                ? value.split(',').map(v => v.trim()).filter(Boolean)
                                                : (value as string[])
                                        );
                                    }}
                                    renderValue={(selected) => (selected as string[]).join(', ')}
                                >
                                    <MenuItem value="Tele">Tele</MenuItem>
                                    <MenuItem value="Wellness">Wellness</MenuItem>
                                    <MenuItem value="Shop">Shop</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Type"
                                value={newTypeField}
                                onChange={e => setNewTypeField(e.target.value)}
                                fullWidth
                                size="small"
                                helperText="Internal type/category identifier"
                            />
                            <TextField
                                label="Info / Description"
                                value={newInfo}
                                onChange={e => setNewInfo(e.target.value)}
                                fullWidth
                                size="small"
                                multiline
                                minRows={2}
                            />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newPublic}
                                            onChange={e => setNewPublic(e.target.checked)}
                                        />
                                    }
                                    label="Public"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newValid}
                                            onChange={e => setNewValid(e.target.checked)}
                                        />
                                    }
                                    label="Valid"
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                if (!creating) setAddDialogOpen(false);
                            }}
                            disabled={creating}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleCreateCoupon}
                            disabled={creating}
                        >
                            {creating ? 'Saving...' : 'Create Coupon'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={dialogOpen && Boolean(selectedCoupon)}
                    onClose={() => setDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {selectedCoupon?.couponName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Coupon details
                                </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => setDialogOpen(false)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent dividers>
                        {selectedCoupon && (
                            <Box>
                                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        label={selectedCoupon.Public ? 'Public' : 'Private'}
                                        size="small"
                                        color={selectedCoupon.Public ? 'primary' : 'default'}
                                    />
                                    <Chip
                                        label={selectedCoupon.couponType}
                                        size="small"
                                        variant="outlined"
                                    />
                                    {selectedCoupon.expiration && (
                                        <Chip
                                            label={`Expires: ${new Date(selectedCoupon.expiration).toLocaleDateString()}`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>

                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, width: '35%' }}>ID</TableCell>
                                            <TableCell>{selectedCoupon.id}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                            <TableCell>{selectedCoupon.couponType}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                                            <TableCell>
                                                {selectedCoupon.couponType === 'percentage'
                                                    ? selectedCoupon.discount
                                                        ? `${selectedCoupon.discount}%`
                                                        : '-'
                                                    : selectedCoupon.cashRedeem
                                                        ? `₹${selectedCoupon.cashRedeem}`
                                                        : '-'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Min / Max order</TableCell>
                                            <TableCell>
                                                {selectedCoupon.minValue
                                                    ? `Min: ₹${selectedCoupon.minValue}`
                                                    : 'Min: -'}
                                                {'  '}
                                                {selectedCoupon.maxUsage
                                                    ? `| Max usage: ${selectedCoupon.maxUsage}`
                                                    : ''}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Applicable To</TableCell>
                                            <TableCell>
                                                {selectedCoupon.applicableTo &&
                                                    selectedCoupon.applicableTo.length > 0
                                                    ? selectedCoupon.applicableTo.join(', ')
                                                    : '-'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={selectedCoupon.valid ? 'Valid' : 'Invalid'}
                                                    size="small"
                                                    color={selectedCoupon.valid ? 'success' : 'default'}
                                                    variant={selectedCoupon.valid ? 'filled' : 'outlined'}
                                                />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Expiration</TableCell>
                                            <TableCell>
                                                {selectedCoupon.expiration
                                                    ? new Date(selectedCoupon.expiration).toLocaleString()
                                                    : 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            size="small"
                            onClick={handleCopyCode}
                            disabled={!selectedCoupon?.couponName}
                        >
                            Copy Code
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                if (selectedCoupon) openEditDialogFromCoupon(selectedCoupon);
                            }}
                            disabled={!selectedCoupon}
                        >
                            Edit
                        </Button>
                        {copyMessage && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {copyMessage}
                            </Typography>
                        )}
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', pr: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
                                Click outside or Close to dismiss
                            </Typography>
                        </Box>
                    </DialogActions>
                </Dialog>
                {/* Edit Coupon Dialog */}
                <Dialog
                    open={editDialogOpen && Boolean(selectedCoupon)}
                    onClose={() => {
                        if (!editSaving) setEditDialogOpen(false);
                    }}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Edit Coupon
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    if (!editSaving) setEditDialogOpen(false);
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent dividers>
                        {editError && (
                            <Box sx={{ mb: 2 }}>
                                <Alert severity="error">{editError}</Alert>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Coupon Name"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="User ID (optional)"
                                value={editUserId}
                                onChange={e => setEditUserId(e.target.value)}
                                fullWidth
                                size="small"
                            />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <FormControl sx={{ flex: '1 1 160px', minWidth: 140 }} size="small">
                                    <InputLabel>Coupon Type</InputLabel>
                                    <Select
                                        label="Coupon Type"
                                        value={editCouponType}
                                        onChange={e =>
                                            setEditCouponType(e.target.value as 'fixedAmount' | 'percentage')
                                        }
                                    >
                                        <MenuItem value="percentage">Percentage</MenuItem>
                                        <MenuItem value="fixedAmount">Fixed Amount</MenuItem>
                                    </Select>
                                </FormControl>
                                {editCouponType === 'percentage' ? (
                                    <TextField
                                        label="Discount %"
                                        type="number"
                                        value={editDiscount}
                                        onChange={e => setEditDiscount(e.target.value)}
                                        size="small"
                                        sx={{ flex: '1 1 160px', minWidth: 140 }}
                                    />
                                ) : (
                                    <>
                                        <TextField
                                            label="Cash Redeem (₹)"
                                            type="number"
                                            value={editCashRedeem}
                                            onChange={e => setEditCashRedeem(e.target.value)}
                                            size="small"
                                            sx={{ flex: '1 1 160px', minWidth: 140 }}
                                        />
                                        <TextField
                                            label="Min Order Value (₹)"
                                            type="number"
                                            value={editMinValue}
                                            onChange={e => setEditMinValue(e.target.value)}
                                            size="small"
                                            sx={{ flex: '1 1 160px', minWidth: 140 }}
                                        />
                                    </>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <TextField
                                    label="Max Usage"
                                    type="number"
                                    value={editMaxUsage}
                                    onChange={e => setEditMaxUsage(e.target.value)}
                                    size="small"
                                    sx={{ flex: '1 1 160px', minWidth: 140 }}
                                />
                                <DatePicker
                                    label="Expiration Date"
                                    value={editExpirationDate}
                                    onChange={(date) => setEditExpirationDate(date)}
                                    slotProps={{ textField: { size: 'small', sx: { flex: '2 1 220px', minWidth: 200 } } }}
                                />
                            </Box>
                            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                <InputLabel>Applicable To</InputLabel>
                                <Select
                                    label="Applicable To"
                                    multiple
                                    value={editApplicableTo}
                                    onChange={e => {
                                        const value = e.target.value;
                                        setEditApplicableTo(
                                            typeof value === 'string'
                                                ? value.split(',').map(v => v.trim()).filter(Boolean)
                                                : (value as string[])
                                        );
                                    }}
                                    renderValue={(selected) => (selected as string[]).join(', ')}
                                >
                                    <MenuItem value="Tele">Tele</MenuItem>
                                    <MenuItem value="Wellness">Wellness</MenuItem>
                                    <MenuItem value="Shop">Shop</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Type"
                                value={editTypeField}
                                onChange={e => setEditTypeField(e.target.value)}
                                fullWidth
                                size="small"
                                helperText="Internal type/category identifier"
                            />
                            <TextField
                                label="Info / Description"
                                value={editInfo}
                                onChange={e => setEditInfo(e.target.value)}
                                fullWidth
                                size="small"
                                multiline
                                minRows={2}
                            />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={editPublic}
                                            onChange={e => setEditPublic(e.target.checked)}
                                        />
                                    }
                                    label="Public"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={editValid}
                                            onChange={e => setEditValid(e.target.checked)}
                                        />
                                    }
                                    label="Valid"
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                if (!editSaving) setEditDialogOpen(false);
                            }}
                            disabled={editSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disabled={editSaving || !selectedCoupon}
                            onClick={async () => {
                                if (!selectedCoupon) return;
                                setEditSaving(true);
                                setEditError(null);
                                try {
                                    const payload: any = { id: selectedCoupon.id };

                                    if (editName.trim() && editName.trim() !== selectedCoupon.couponName) {
                                        payload.couponName = editName.trim();
                                    }
                                    if (editUserId.trim() !== (selectedCoupon.userId || '')) {
                                        payload.userId = editUserId.trim();
                                    }
                                    if (editCouponType !== (selectedCoupon.couponType === 'fixedAmount' ? 'fixedAmount' : 'percentage')) {
                                        payload.couponType = editCouponType;
                                    }
                                    if (editCouponType === 'percentage') {
                                        if (editDiscount) {
                                            payload.discount = Number(editDiscount);
                                        }
                                    } else {
                                        if (editCashRedeem) {
                                            payload.cashRedeem = Number(editCashRedeem);
                                        }
                                        if (editMinValue) {
                                            payload.minValue = Number(editMinValue);
                                        }
                                    }
                                    if (editMaxUsage) {
                                        payload.maxUsage = Number(editMaxUsage);
                                    }
                                    if (editApplicableTo.length > 0) {
                                        payload.applicableTo = editApplicableTo;
                                    }
                                    if (editTypeField) {
                                        payload.type = editTypeField;
                                    }
                                    if (typeof editValid === 'boolean') {
                                        payload.valid = editValid;
                                    }
                                    if (typeof editPublic === 'boolean') {
                                        payload.Public = editPublic;
                                    }
                                    if (editInfo) {
                                        payload.info = editInfo;
                                    }
                                    if (editExpirationDate) {
                                        payload.expiration = editExpirationDate.toISOString();
                                    }

                                    const res = await shopModel.post('/editCoupons', payload);
                                    const updated: Coupon = res.data;
                                    setCoupons(prev => prev.map(c => (c.id === updated.id ? updated : c)));
                                    setSelectedCoupon(updated);
                                    setEditDialogOpen(false);
                                } catch (err: any) {
                                    setEditError(
                                        err.response?.data?.error ||
                                        err.message ||
                                        'Failed to update coupon'
                                    );
                                } finally {
                                    setEditSaving(false);
                                }
                            }}
                        >
                            {editSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Confirm Delete Dialog */}
                <Dialog
                    open={confirmDeleteDialogOpen}
                    onClose={() => {
                        if (!deleting) setConfirmDeleteDialogOpen(false);
                    }}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}
                >
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        {deleteError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {deleteError}
                            </Alert>
                        )}
                        <Typography>
                            Are you sure you want to delete {selectedIds.size} coupon(s)? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setConfirmDeleteDialogOpen(false)}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDeleteSelected}
                            disabled={deleting}
                        >
                            {deleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default CouponSections;